/**
 * `<AuthProvider />` + `useAuth()` — wraps the dashboard's
 * authenticated-user state (ADR-0018 §7).
 *
 * Three responsibilities:
 *
 *   1. **Bootstrap auth check** on first render: call `api.auth.
 *      currentUser()` against the real API (or the mock in tests).
 *      200 → hydrate `currentUser`. 401 → route to `'login'` view.
 *      Network / 5xx → route to `'login'` with a "could not reach
 *      the service" soft-error state.
 *
 *   2. **In-flight 401 routing**: the HTTP client's `on_unauthorized`
 *      callback (wired in `providers.tsx`) triggers `clearUser()` +
 *      the navigation to login. Single source of truth for the
 *      session-expired UX.
 *
 *   3. **Expose `currentUser`** via `useAuth()` for views (TopBar,
 *      account dropdown, etc.) to read.
 *
 * The bootstrap is intentionally synchronous-state-first — a loading
 * splash renders before the check resolves so we don't flash an
 * authenticated layout to a soon-to-be-signed-out user.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { User } from '@sill/types';
import { AuthUnauthorizedError } from '@sill/types/api';

import { useApi } from '../api/api-context.js';
import type { View } from './view-router.js';

export type AuthState =
  | { kind: 'bootstrap' }
  | { kind: 'authenticated'; user: User }
  | { kind: 'unauthenticated' }
  | { kind: 'service_error'; diagnostic?: string };

/** Last bootstrap attempt details, for the "taking longer" screen — what
 *  stalled and for how long, so a report from a phone is actionable. */
export const BOOTSTRAP_DIAG_KEY = 'sill_bootstrap_diag';

export interface AuthCtxValue {
  state: AuthState;
  /**
   * Reload the current user from the API. Used after sign-in /
   * sign-out transitions to re-hydrate the cached state.
   */
  refresh: () => Promise<void>;
  /**
   * Drop the cached user and mark unauthenticated. Called by the
   * HTTP client's `on_unauthorized` and by the sign-out flow.
   */
  clear: () => void;
  /**
   * Full sign-out: POST `/v1/auth/sign-out` to invalidate the server
   * session + cookie, then drop local state and navigate to login.
   * Network errors are swallowed so the local sign-out is never
   * blocked by a flaky API — the cookie will expire or the next
   * `currentUser()` will re-confirm anyway.
   */
  signOut: () => Promise<void>;
}

/**
 * ADR-0185 follow-up (founder report 2026-07-13: a fresh signup link
 * "did not take me anywhere") — the /welcome confirm page is an
 * ANONYMOUS surface: the visitor lands there before any session
 * exists, so the bootstrap `currentUser()` 401 is EXPECTED and must
 * not yank them to the login view (which is exactly what happened).
 * The URL is the same source of truth `initialView()` resolves from.
 */
export function isAnonymousAuthPath(pathname: string): boolean {
  // /welcome (signup confirm) and /verify (magic-link bridge) both run
  // BEFORE a cookie exists; a 401 there is expected and must never
  // route to Login (founder 2026-09-23: the confirm page was replaced by
  // "Sign in — Welcome back." within a second of opening the link).
  //
  // ADR-0216 — /support-login joins the same club. The bootstrap 401
  // fires before the redeem POST lands; without this gate the visitor
  // is bounced to Login one second in and the token is left unspent.
  return (
    pathname.startsWith('/welcome')
    || pathname.startsWith('/verify')
    || pathname.startsWith('/support-login')
    // ADR-0237 — the agency-access approval page gates on an
    // authenticated owner session; an unauthenticated bootstrap must
    // let the view render its own "sign in and click the email link
    // again" copy instead of bouncing to Login (which would drop the
    // one-shot approval token and leave the user confused).
    || pathname.startsWith('/settings/agency-access')
    // ADR-0261 — assistant MCP OAuth consent landing. A signed-out
    // visitor arriving from the assistant's /authorize redirect
    // needs the ConnectApproval view to mount so it can save the
    // return path and render Login in place; a bootstrap 401 must
    // not pre-empt that.
    || pathname.startsWith('/connect/')
  );
}

export const AuthCtx = createContext<AuthCtxValue | null>(null);

export function useAuth(): AuthCtxValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error('useAuth() called outside <AuthProvider>');
  }
  return ctx;
}

/**
 * Same as `useAuth()` but returns `null` when no provider is mounted
 * rather than throwing. Used by leaf features (e.g. `BuyerDetailPanel`)
 * that should gracefully no-op in legacy test scaffolds that haven't
 * wired the full provider tree; production always returns non-null.
 */
export function useOptionalAuth(): AuthCtxValue | null {
  return useContext(AuthCtx);
}

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Called when the bootstrap auth check returns 401 or when an
   * in-flight 401 fires. The provider does NOT navigate directly —
   * it surfaces the signal and the App-level routing handles the
   * actual view change. Same pattern as the other view-control
   * callbacks in `providers.tsx`.
   */
  setView: (v: View) => void;
  /**
   * Optional initial state for tests. Production omits this and the
   * provider boots into `'bootstrap'` then resolves via `api.auth.
   * currentUser()` on first effect.
   */
  initial?: AuthState;
}

export function AuthProvider({
  children,
  setView,
  initial,
}: AuthProviderProps): JSX.Element {
  const api = useApi();
  const [state, setState] = useState<AuthState>(initial ?? { kind: 'bootstrap' });

  const refresh = async (): Promise<void> => {
    // An anonymous auth surface (/welcome confirm page) expects the
    // bootstrap 401 — record the state but leave the view alone so the
    // visitor can complete the confirm.
    const on_anonymous_surface =
      typeof window !== 'undefined' && isAnonymousAuthPath(window.location.pathname);

    // Helper: try once with a 10 s ceiling so a cold Fly.io machine or a
    // hanging TCP connection doesn't keep the bootstrap splash up forever.
    // The underlying fetch continues in the background; we just stop waiting.
    // Non-auth errors (including the timeout) trigger the retry below; a 401
    // is still caught as AuthUnauthorizedError and NOT retried.
    const tryOnce = (): Promise<import('@sill/types').User> => {
      const BOOTSTRAP_TIMEOUT_MS = 10_000;
      const deadline = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('bootstrap_timeout')),
          BOOTSTRAP_TIMEOUT_MS,
        ),
      );
      return Promise.race([api.auth.currentUser(), deadline]);
    };

    let lastErr: unknown;
    const started = Date.now();
    const attempts_log: string[] = [];
    // Three attempts (10 s ceiling each, short pauses) for transient
    // network / 5xx errors. A 401 is definitive (the cookie is absent or
    // the session expired) and is NOT retried — only non-auth errors get
    // another chance so a cold API or a brief DB hiccup doesn't falsely
    // boot the user to the login screen.
    for (let attempt = 0; attempt < 3; attempt++) {
      const t0 = Date.now();
      try {
        const user = await tryOnce();
        setState({ kind: 'authenticated', user });
        return;
      } catch (err) {
        if (err instanceof AuthUnauthorizedError) {
          // Definitive — the session is absent or expired. No retry.
          setState({ kind: 'unauthenticated' });
          if (!on_anonymous_surface) setView('login');
          return;
        }
        lastErr = err;
        attempts_log.push(`#${attempt + 1} ${err instanceof Error ? `${err.name}: ${err.message}` : String(err)} after ${Date.now() - t0} ms`);
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
    }

    // Every attempt failed with a non-auth error (network / 5xx / stall).
    // This is NOT a sign-out: the cookie may be perfectly valid and the
    // request path is what failed. Stay on the splash with the details
    // and a retry (founder report 2026-09-23: a stalled bootstrap right
    // after a magic link looked like "the link sent me back to sign-in").
    void lastErr;
    const diagnostic = [
      `path=${typeof window !== 'undefined' ? window.location.pathname + window.location.search.replace(/token=[^&]+/, 'token=…') : '?'}`,
      `elapsed=${Date.now() - started} ms`,
      ...attempts_log,
      typeof navigator !== 'undefined' ? `ua=${navigator.userAgent.slice(0, 120)}` : '',
    ].filter((l) => l.length > 0).join('\n');
    try { window.sessionStorage.setItem(BOOTSTRAP_DIAG_KEY, diagnostic); } catch { /* ignore */ }
    setState({ kind: 'service_error', diagnostic });
  };

  const clear = (): void => {
    setState({ kind: 'unauthenticated' });
  };

  const signOut = async (): Promise<void> => {
    try {
      await api.auth.signOut();
    } catch {
      // Swallow network / 5xx. The local sign-out is intent enough —
      // the cookie will expire on its own and the next bootstrap
      // `currentUser()` will confirm with the server.
    }
    clear();
    setView('login');
  };

  useEffect(() => {
    if (state.kind !== 'bootstrap') return;
    void refresh();
    // The effect runs once on mount; refresh is stable per-render but
    // we deliberately do NOT depend on it (would re-run on every
    // render). The `state.kind` guard is the single-fire mechanism.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCtx.Provider value={{ state, refresh, clear, signOut }}>{children}</AuthCtx.Provider>
  );
}
